import { api } from './client';
import type { Flight, FlightDocument } from '../types';

export interface FlightStopInput {
  fromAirport: string;
  toAirport: string;
  departureTime: string;
  arrivalTime: string;
  flightNumber?: string | null;
  airline?: string | null;
  reminderMinutes?: number | null;
}

export type FlightInput = Partial<Omit<Flight, 'stops'>> & {
  stops?: FlightStopInput[];
  participantIds?: string[];
};

export interface ExtractedFlightLeg {
  fromAirport:    string;
  toAirport:      string;
  departureTime:  string;
  arrivalTime:    string;
  flightNumber?:  string;
  airline?:       string;
}

export interface ExtractedFlightGroup {
  direction?:      'OUTBOUND' | 'RETURN';
  bookingRef?:     string;
  passengerNames?: string[];
  legs:            ExtractedFlightLeg[];
}

export interface ExtractedFlightData {
  flights:               ExtractedFlightGroup[];
  aiCreditsLeft:         number;
  premiumAiCreditsLeft:  number;
}

export const flightsApi = {
  list: (tripId: string) =>
    api.get<Flight[]>(`/trips/${tripId}/flights`).then(r => r.data),
  create: (tripId: string, data: FlightInput) =>
    api.post<Flight>(`/trips/${tripId}/flights`, data).then(r => r.data),
  update: (tripId: string, flightId: string, data: FlightInput) =>
    api.patch<Flight>(`/trips/${tripId}/flights/${flightId}`, data).then(r => r.data),
  delete: (tripId: string, flightId: string) =>
    api.delete(`/trips/${tripId}/flights/${flightId}`),
  uploadDocument: (tripId: string, flightId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<FlightDocument>(
      `/trips/${tripId}/flights/${flightId}/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },
  deleteDocument: (tripId: string, flightId: string, docId: string) =>
    api.delete(`/trips/${tripId}/flights/${flightId}/documents/${docId}`),
  extract: (tripId: string, files: File[]) => {
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    return api.post<ExtractedFlightData>(
      `/trips/${tripId}/flights/extract`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },
};
